"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type * as React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { Icon } from "./icon";
import { selectTriggerIconClassName, selectTriggerVariants } from "./select";
import { Text } from "./text";

export type FieldTriggerProps = useRender.ComponentProps<"button"> &
  Omittable<{
    /** The held value. Omit it to show the placeholder in muted ink. */
    value?: React.ReactNode;
    /** What an empty trigger says. Defaults to the em dash: the row's own
     *  label already names the field, so a "Select a…" line would restate it. */
    placeholder?: React.ReactNode;
    /** A visual before the value — a swatch strip, a glyph. The trigger pulls
     *  its leading inset in so the visual's edge, not the text's, aligns. */
    leading?: React.ReactNode;
    /** Spans the field column (`full`) or hugs the value (`auto`). */
    width?: "auto" | "full";
  }>;

/**
 * A popover or menu trigger with a select field's exact anatomy — the
 * hairline, the fill, the value on the left, the stacked chevrons on the
 * right — for a control that opens something a `Select` cannot hold (a
 * searchable list, a catalog of swatches) yet must be indistinguishable from
 * the selects it sits among.
 */
export function FieldTrigger({
  className,
  value,
  placeholder = "\u2014",
  leading,
  width = "full",
  render,
  // The anatomy IS the children. A host that mounts this through `render`
  // (a popover trigger) forwards its own empty `children`, which must not
  // replace the value.
  children: _children,
  ...props
}: FieldTriggerProps): React.ReactElement {
  const empty = value == null || value === "";
  const defaultProps = {
    children: (
      <>
        {leading}
        <Text
          align="left"
          className="flex-1"
          size="sm"
          truncate
          variant={empty ? "muted" : undefined}
        >
          {empty ? placeholder : value}
        </Text>
        <Icon className={selectTriggerIconClassName} name="chevrons-up-down" />
      </>
    ),
    className: cn(
      selectTriggerVariants({ variant: "default", width }),
      width === "auto" && "min-w-36",
      leading != null && "ps-1.5",
      className,
    ),
    "data-slot": "field-trigger",
    type: render ? undefined : ("button" as const),
  };
  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(defaultProps, props),
    render,
  });
}
